import { create } from 'zustand';
import type { Stage } from '@/features/orders/lib';

/**
 * Browsing state of the journeys list that has no business in the URL —
 * sort, grouping, the free-text filter, and which stage groups are collapsed.
 *
 * The pieces that DO identify what you are looking at moved to the router when
 * this app was lifted out of sundae: the view is the route, the stage filter is
 * `?stage=`, and the expanded journey is `/journeys/:id`. What is left here is
 * exactly the state that should survive navigation without being shareable.
 */
export type OrdersSortBy = 'date' | 'vendor' | 'status';
export type OrdersGroupBy = 'stage' | 'vendor' | 'none';
export type OrdersStageFilter = 'all' | Stage;

type Updater<T> = T | ((prev: T) => T);
function resolve<T>(next: Updater<T>, prev: T): T {
  return typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
}

// Finished journeys stay reachable but out of the way.
const DEFAULT_COLLAPSED: Partial<Record<string, boolean>> = { done: true, cancelled: true };

interface OrdersViewState {
  search: string;
  sortBy: OrdersSortBy;
  groupBy: OrdersGroupBy;
  collapsed: Partial<Record<string, boolean>>;

  setSearch: (search: string) => void;
  setSortBy: (sortBy: OrdersSortBy) => void;
  setGroupBy: (groupBy: OrdersGroupBy) => void;
  setCollapsed: (collapsed: Updater<Partial<Record<string, boolean>>>) => void;
}

export const useOrdersViewStore = create<OrdersViewState>()((set) => ({
  search: '',
  sortBy: 'date',
  groupBy: 'stage',
  collapsed: DEFAULT_COLLAPSED,

  setSearch: (search) => set({ search }),
  setSortBy: (sortBy) => set({ sortBy }),
  setGroupBy: (groupBy) => set({ groupBy }),
  setCollapsed: (collapsed) => set((s) => ({ collapsed: resolve(collapsed, s.collapsed) })),
}));

/**
 * Scroll-restoration scope for the one shared scroll container. Every work
 * view is its own list; the journeys list is a different list per
 * filter/sort/grouping combination, so a filter change starts at the top and
 * switching back to a previous combination returns to its own position.
 */
export function ordersScrollScope(s: {
  view: string;
  stageFilter: OrdersStageFilter;
  includeArchived: boolean;
  sortBy: OrdersSortBy;
  groupBy: OrdersGroupBy;
  search: string;
}): string {
  if (s.view !== 'orders') return `orders:${s.view}`;
  return `orders:orders:${s.stageFilter}:${s.groupBy}:${s.sortBy}:${s.includeArchived ? 'archived' : 'active'}:${s.search.trim().toLowerCase()}`;
}
